import React, { useState, ChangeEvent, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CircularProgress } from '@mui/material'
import { useSelector } from 'react-redux'

import {
  selectLoading,
  selectError,
} from '../../redux/keycloak/keycloakSelectors'
import { registerUser } from '../../redux/keycloak/register'
import { useAppDispatch } from '../../redux/store'
import CelebrationScreen from '../../components/containers/CelebrationScreen'
import {
  AuthPasswordInput,
  AuthShell,
  passwordStrength,
} from './AuthShell'

type FormData = {
  firstName: string
  lastName: string
  username: string
  email: string
  password: string
  confirmPassword: string
}

const RegisterPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const loading = useSelector(selectLoading)
  const error = useSelector(selectError)

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [validationError, setValidationError] = useState('')
  const [showCelebration, setShowCelebration] = useState(false)

  const { rules, score } = passwordStrength(formData.password)

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleRegister = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    setValidationError('')

    if (formData.password !== formData.confirmPassword) {
      setValidationError(t('auth.passwordMismatch'))
      return
    }
    if (!acceptTerms) {
      setValidationError(t('auth.acceptTermsRequired'))
      return
    }

    try {
      const resultAction = await dispatch(
        registerUser({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
        })
      )

      if (registerUser.fulfilled.match(resultAction)) {
        setShowCelebration(true)
        setTimeout(() => navigate('/login'), 3000)
      }
    } catch (err) {
      console.log('err', err)
    }
  }

  const getTranslatedError = () => {
    if (validationError) return validationError
    if (!error) return ''
    if (error.includes('Password must not be the same as username or email')) {
      return t('auth.passwordSameAsCredentials')
    }
    if (error.includes('Password must contain at least 12 characters')) {
      return t('auth.passwordComplexity')
    }
    if (error.includes('Username already exists')) {
      return t('auth.usernameExists')
    }
    if (error.includes('Email already exists')) {
      return t('auth.emailExists')
    }
    return t('auth.registrationFailed')
  }

  if (showCelebration) {
    return <CelebrationScreen message={t('auth.successfulRegistration')} />
  }

  const displayError = getTranslatedError()

  return (
    <AuthShell
      wide
      foot={
        <>
          {t('auth.alreadyHaveAccountShort')}{' '}
          <button
            type="button"
            className="incyia-auth-link"
            onClick={() => navigate('/login')}
          >
            {t('auth.signIn')}
          </button>
        </>
      }
    >
      <h1 className="incyia-auth-title">{t('auth.createAccount')}</h1>
      <p className="incyia-auth-sub">{t('auth.createAccountSub')}</p>

      {displayError ? (
        <div className="incyia-auth-error">{displayError}</div>
      ) : null}

      <form onSubmit={handleRegister}>
        <div className="incyia-auth-grid">
          <div className="incyia-auth-field">
            <label className="incyia-auth-label" htmlFor="reg-first">
              {t('auth.firstName')}
              <span className="incyia-auth-req">*</span>
            </label>
            <input
              id="reg-first"
              className="incyia-auth-inp"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder={t('auth.firstNamePlaceholder')}
              required
            />
          </div>
          <div className="incyia-auth-field">
            <label className="incyia-auth-label" htmlFor="reg-last">
              {t('auth.lastName')}
              <span className="incyia-auth-req">*</span>
            </label>
            <input
              id="reg-last"
              className="incyia-auth-inp"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder={t('auth.lastNamePlaceholder')}
              required
            />
          </div>

          <div className="incyia-auth-field incyia-auth-field--span">
            <label className="incyia-auth-label" htmlFor="reg-username">
              {t('auth.username')}
              <span className="incyia-auth-req">*</span>
            </label>
            <input
              id="reg-username"
              className="incyia-auth-inp"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder={t('auth.usernamePlaceholder')}
              autoComplete="username"
              required
            />
            <span className="incyia-auth-help">{t('auth.usernameHelp')}</span>
          </div>

          <div className="incyia-auth-field incyia-auth-field--span">
            <label className="incyia-auth-label" htmlFor="reg-email">
              {t('auth.workEmail')}
              <span className="incyia-auth-req">*</span>
            </label>
            <input
              id="reg-email"
              className="incyia-auth-inp"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('auth.emailPlaceholder')}
              autoComplete="email"
              required
            />
            <span className="incyia-auth-help">{t('auth.emailHelp')}</span>
          </div>

          <div className="incyia-auth-field incyia-auth-field--span">
            <label className="incyia-auth-label" htmlFor="reg-password">
              {t('auth.password')}
              <span className="incyia-auth-req">*</span>
            </label>
            <AuthPasswordInput
              id="reg-password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t('auth.passwordPlaceholder')}
              autoComplete="new-password"
              required
            />
            <div
              className={`incyia-auth-pw-meter${score ? ` s${score}` : ''}`}
              aria-hidden
            >
              <span />
              <span />
              <span />
              <span />
            </div>
            <div className="incyia-auth-pw-rules">
              <span className={rules.len ? 'is-ok' : ''}>
                {t('auth.pwRuleLen')}
              </span>
              <span className={rules.up ? 'is-ok' : ''}>
                {t('auth.pwRuleCase')}
              </span>
              <span className={rules.num ? 'is-ok' : ''}>
                {t('auth.pwRuleNum')}
              </span>
              <span className={rules.sym ? 'is-ok' : ''}>
                {t('auth.pwRuleSym')}
              </span>
            </div>
          </div>

          <div className="incyia-auth-field incyia-auth-field--span">
            <label className="incyia-auth-label" htmlFor="reg-confirm">
              {t('auth.confirmPassword')}
              <span className="incyia-auth-req">*</span>
            </label>
            <input
              id="reg-confirm"
              className="incyia-auth-inp"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t('auth.confirmPasswordPlaceholder')}
              autoComplete="new-password"
              required
            />
          </div>
        </div>

        <label className="incyia-auth-check" style={{ marginTop: 16 }}>
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
          />
          <span>{t('auth.acceptTerms')}</span>
        </label>

        <button
          type="submit"
          className="incyia-auth-submit"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: '#fff' }} />
          ) : (
            t('auth.requestAccount')
          )}
        </button>
      </form>
    </AuthShell>
  )
}

export default RegisterPage
